import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Sprout, Beef, Shield, Users, Smartphone, Building2, TrendingUp, Clock, Award, MapPin, ArrowRight, ChevronRight } from 'lucide-react';
import { mockTestimonials, mockServices, mockStats, mockBranches } from '../mock';
import AgroIAChat from './AgroIAChat';

const iconMap = {
  Sprout,
  Beef,
  Shield,
  Users,
  Smartphone,
  Building2
};

const HomePage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % mockTestimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* AgroIA Floating Chat */}
      <AgroIAChat />

      {/* Header/Navbar */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 transition-all duration-300">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 group cursor-pointer">
              <img 
                src="https://customer-assets.emergentagent.com/job_5732c38f-46bd-4a56-9931-c3cf86a70f4f/artifacts/nfkada7y_logo.jpg" 
                alt="Banco Agrícola" 
                className="h-12 w-auto transition-transform duration-300 group-hover:scale-105" 
              />
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#servicios" className="text-gray-700 hover:text-green-600 transition-all duration-300 font-medium relative group">
                Servicios
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#beneficios" className="text-gray-700 hover:text-green-600 transition-all duration-300 font-medium relative group">
                Beneficios
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#testimonios" className="text-gray-700 hover:text-green-600 transition-all duration-300 font-medium relative group">
                Testimonios
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#sucursales" className="text-gray-700 hover:text-green-600 transition-all duration-300 font-medium relative group">
                Sucursales
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <Link to="/login">
                <Button className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300 hover:shadow-lg hover:scale-105">
                  Ingresar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-green-50">
          <div 
            className="absolute top-20 left-10 w-72 h-72 bg-green-200/30 rounded-full blur-3xl"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          ></div>
          <div 
            className="absolute bottom-20 right-10 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"
            style={{ transform: `translateY(${-scrollY * 0.2}px)` }}
          ></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-in fade-in slide-in-from-left duration-700">
              <div className="inline-block">
                <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold animate-in fade-in slide-in-from-left delay-100">
                  Banco Universal
                </span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                <span className="text-green-600 block mb-2">
                  Sembrando Soberanía
                </span>
                El banco que hace crecer tu campo
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Neo banco híbrido digital y físico especializado en el sector agrícola y ganadero de Venezuela. Financiamiento inteligente con atención personalizada.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/onboarding">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 group">
                    Abrir Cuenta
                    <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/onboarding">
                  <Button size="lg" variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:scale-105">
                    Solicitar Crédito
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="transform hover:scale-110 transition-transform duration-300">
                  <div className="text-3xl font-bold text-gray-900">15K+</div>
                  <div className="text-sm text-gray-600">Productores</div>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div className="transform hover:scale-110 transition-transform duration-300">
                  <div className="text-3xl font-bold text-gray-900">Bs. 9.7B+</div>
                  <div className="text-sm text-gray-600">Financiados</div>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div className="transform hover:scale-110 transition-transform duration-300">
                  <div className="text-3xl font-bold text-gray-900">50+</div>
                  <div className="text-sm text-gray-600">Sucursales</div>
                </div>
              </div>
            </div>
            <div className="relative animate-in fade-in slide-in-from-right duration-700 delay-200">
              <div className="absolute inset-0 bg-gradient-to-br from-green-200/40 to-green-400/40 blur-3xl animate-pulse"></div>
              <img 
                src="https://images.unsplash.com/photo-1585335622299-0b275237536a" 
                alt="Agricultor venezolano en su finca" 
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {mockStats.map((stat, index) => (
              <div 
                key={stat.id} 
                className="text-center transform hover:scale-110 transition-all duration-300 cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl lg:text-5xl font-bold mb-2 animate-in fade-in zoom-in">{stat.value}</div>
                <div className="text-green-100 text-sm lg:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 bg-gradient-to-b from-white to-green-50/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-500">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Servicios Especializados</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Soluciones financieras diseñadas específicamente para el sector agropecuario venezolano
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="border-green-100 hover:border-green-300 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group cursor-pointer animate-in fade-in slide-in-from-bottom"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-600 transition-colors duration-300">
                      <Icon className="w-7 h-7 text-green-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-green-600 transition-colors duration-300">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 animate-in fade-in slide-in-from-left duration-700">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-green-600/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                <img 
                  src="https://images.unsplash.com/photo-1713952152768-5f28b8093166" 
                  alt="Tecnología agrícola" 
                  className="relative rounded-3xl shadow-2xl w-full h-[450px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
            <div className="space-y-6 order-1 lg:order-2 animate-in fade-in slide-in-from-right duration-700">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">¿Por qué elegirnos?</h2>
              <div className="space-y-4">
                {[
                  { icon: TrendingUp, title: 'Tasas Competitivas', desc: 'Las mejores tasas del mercado para créditos agrícolas y ganaderos.' },
                  { icon: Clock, title: 'Respuesta Rápida', desc: 'Aprobación de créditos en 48 horas con documentación mínima.' },
                  { icon: Award, title: 'Asesoría Experta', desc: 'Agrónomos y especialistas te acompañan en cada etapa.' },
                  { icon: Smartphone, title: '100% Digital', desc: 'Gestiona todo desde tu móvil con atención física disponible.' }
                ].map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex gap-4 p-4 rounded-xl hover:bg-green-50 transition-all duration-300 transform hover:translate-x-2 cursor-pointer group"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:rotate-6">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors duration-300">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" className="py-20 bg-gradient-to-b from-green-50/30 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-500">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Historias de Éxito</h2>
            <p className="text-xl text-gray-600">Lo que dicen nuestros clientes del campo</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id} 
                className="border-green-100 hover:border-green-300 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer animate-in fade-in zoom-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-green-100 hover:ring-green-300 transition-all duration-300" 
                    />
                    <div>
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <CardDescription>{testimonial.role}</CardDescription>
                    </div>
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">"{testimonial.text}"</p>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section id="sucursales" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-500">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Nuestras Sucursales</h2>
            <p className="text-xl text-gray-600">Presencia en todo el territorio nacional</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockBranches.map((branch, index) => (
              <Card 
                key={branch.id} 
                className="border-green-100 hover:border-green-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-green-600 transition-colors duration-300">
                    <MapPin className="w-5 h-5 text-green-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-lg">{branch.name}</CardTitle>
                  <CardDescription className="space-y-1">
                    <p>{branch.address}</p>
                    <p className="font-medium text-green-600">{branch.phone}</p>
                    <p className="text-xs">{branch.hours}</p>
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 via-green-700 to-green-600 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="bg-white rounded-2xl p-4 inline-block mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_5732c38f-46bd-4a56-9931-c3cf86a70f4f/artifacts/nfkada7y_logo.jpg" 
              alt="Banco Agrícola" 
              className="h-16 w-auto" 
            />
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 animate-in fade-in zoom-in duration-500">¿Listo para hacer crecer tu negocio agrícola?</h2>
          <p className="text-xl mb-8 text-green-100">Abre tu cuenta hoy y accede a financiamiento inmediato. Sembrando Soberanía.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/onboarding">
              <Button size="lg" className="bg-white text-green-600 hover:bg-green-50 px-8 py-6 text-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
                Comenzar Ahora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
              Hablar con un Asesor
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="bg-white rounded-lg p-2 inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5732c38f-46bd-4a56-9931-c3cf86a70f4f/artifacts/nfkada7y_logo.jpg" 
                  alt="Banco Agrícola" 
                  className="h-10 w-auto" 
                />
              </div>
              <p className="text-sm text-green-400 font-semibold mb-2">Sembrando Soberanía</p>
              <p className="text-sm">Banco Universal especializado en el sector agropecuario venezolano.</p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Servicios</h3>
              <ul className="space-y-2 text-sm">
                <li className="hover:text-green-400 transition-colors cursor-pointer">Créditos Agrícolas</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Créditos Ganaderos</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Seguros</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Online Banking</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Empresa</h3>
              <ul className="space-y-2 text-sm">
                <li className="hover:text-green-400 transition-colors cursor-pointer">Sobre Nosotros</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Sucursales</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Contáctanos</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Trabaja con Nosotros</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li className="hover:text-green-400 transition-colors cursor-pointer">Términos y Condiciones</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Política de Privacidad</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">SUDEBAN</li>
                <li>
                  <Link to="/inversion" className="hover:text-green-400 transition-colors flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Inversión
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>© 2025 Banco Agrícola de Venezuela. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;